import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import axiosInstance from "../../utils/networkWrapper";
import type { NurseShiftLineageEntry } from "./shiftLineage";

const NURSE_SHIFT_LINEAGE_QUERY_KEY = "nurse-shift-lineage";

/**
 * Sync decisions about ONE nurse's shifts/punches (the "Shifts & Punches" sub-tab). Mirrors
 * `useEmployeeChangelogQuery`: `axiosInstance`, TanStack Query v5, the chosen department, the page's
 * start/end range, and the nurse id. The endpoint REQUIRES a nurse, so the query stays disabled until
 * both a department and a nurse are present.
 */
export const useNurseShiftLineageQuery = ({
	start,
	end,
	nurseId,
}: {
	start: DateTime;
	end: DateTime;
	nurseId?: number;
}): UseQueryResult<NurseShiftLineageEntry[], Error> => {
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();

	return useQuery({
		queryKey: [NURSE_SHIFT_LINEAGE_QUERY_KEY, chosenDepartment?.department_id, start.toString(), end.toString(), nurseId],
		queryFn: async () => {
			trackEvent("nurse_shift_lineage_fetch_start", { department_id: chosenDepartment?.department_id, nurse_id: nurseId });

			const { data } = await axiosInstance.get<NurseShiftLineageEntry[]>(`/employee-changelog/departments/${chosenDepartment.department_id}/shift-lineage`, {
				params: {
					nurse_id: nurseId,
					start: start.toFormat("yyyy-MM-dd"),
					end: end.toFormat("yyyy-MM-dd"),
				},
			});

			trackEvent("nurse_shift_lineage_fetch_success", { department_id: chosenDepartment?.department_id, nurse_id: nurseId, entry_count: data.length });
			return data;
		},
		staleTime: 60 * 1000,
		throwOnError: false,
		enabled: !!chosenDepartment?.department_id && nurseId !== undefined,
		meta: {
			onError: (error: unknown) => {
				showErrorSnackbar("Failed to load shift & punch lineage", { action: "failed to fetch nurse shift lineage" }, error);
				trackErrorEvent("nurse_shift_lineage_fetch_error", error, {
					department_id: chosenDepartment?.department_id,
					nurse_id: nurseId,
					error_source: "API",
				});
			},
		},
	});
};
